import { CHART_COLORS } from '@/constants/chartColors'
import { formatCurrency } from '@/utils/function'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { Download } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Button, Typogaphy } from 'vnetwork-ui'
// import { SCHEMES } from '@/components/EchartCustomized' // Reserved for future chart customization

interface RevenueBreakdownData {
    name: string
    renewal: number
    newSale: number
    upsell: number
}

interface CombinedBreakdownData {
    name: string
    current: number
    comparison: number
    color: string
}

interface RevenueBreakdownChartProps {
    data: RevenueBreakdownData[]
    combinedData?: CombinedBreakdownData[]
    currentPeriodLabel?: string
    comparePeriodLabel?: string
    hasComparison?: boolean
}

const RevenueBreakdownChart = ({
    data,
    combinedData = [],
    currentPeriodLabel,
    comparePeriodLabel,
    hasComparison = false,
}: RevenueBreakdownChartProps) => {
    const { t } = useTranslation()
    const chartRef = useRef<HTMLDivElement>(null)

    const exportChartToPDF = async () => {
        if (!chartRef.current) return

        try {
            const canvas = await html2canvas(chartRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
            })

            const imgData = canvas.toDataURL('image/png')
            const pdf = new jsPDF('l', 'mm', 'a4')
            const pdfWidth = pdf.internal.pageSize.getWidth()
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width

            pdf.addImage(imgData, 'PNG', 0, 10, pdfWidth, pdfHeight)
            pdf.save(`revenue-breakdown-${dayjs().format('YYYY-MM-DD')}.pdf`)
        } catch (error) {
            console.error('Export PDF error:', error)
        }
    }

    const formatYAxis = (value: number) => {
        const maxValue = Math.max(
            ...data.flatMap((d) => [d.renewal, d.newSale, d.upsell])
        )
        if (maxValue >= 1_000_000_000) {
            return `${(value / 1_000_000_000).toFixed(1)}B`
        } else if (maxValue >= 1_000_000) {
            return `${(value / 1_000_000).toFixed(1)}M`
        } else {
            return `${(value / 1_000).toFixed(0)}K`
        }
    }

    const CustomXAxisTick = ({ x, y, payload }: any) => {
        const maxLength = 12
        const text = payload.value || ''
        const truncatedText =
            text.length > maxLength
                ? text.substring(0, maxLength) + '...'
                : text

        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    x={0}
                    y={0}
                    dy={16}
                    textAnchor="end"
                    fill="#666"
                    fontSize={10}
                    transform="rotate(-45)"
                >
                    <title>{text}</title>
                    {truncatedText}
                </text>
            </g>
        )
    }

    const CustomBarTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                    <p className="font-medium mb-2">
                        {payload[0].payload.name}
                    </p>
                    {payload.map((entry: any, index: number) => (
                        <p
                            key={index}
                            className="text-sm"
                            style={{ color: entry.color }}
                        >
                            {entry.name}: {formatCurrency(entry.value)}
                        </p>
                    ))}
                </div>
            )
        }
        return null
    }

    const ComparisonTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const current = payload.find((p: any) => p.dataKey === 'current')
            const comparison = payload.find(
                (p: any) => p.dataKey === 'comparison'
            )
            const change =
                current && comparison && comparison.value > 0
                    ? (
                          ((current.value - comparison.value) /
                              comparison.value) *
                          100
                      ).toFixed(1)
                    : null
            const serviceColor = payload[0].payload.color

            return (
                <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                    <p
                        className="font-medium mb-2"
                        style={{ color: serviceColor }}
                    >
                        {payload[0].payload.name}
                    </p>
                    {current && (
                        <p className="text-sm" style={{ color: serviceColor }}>
                            {currentPeriodLabel}:{' '}
                            {formatCurrency(current.value)}
                        </p>
                    )}
                    {comparison && (
                        <p className="text-sm text-gray-500">
                            {comparePeriodLabel}:{' '}
                            {formatCurrency(comparison.value)}
                        </p>
                    )}
                    {change && (
                        <p
                            className={`text-sm font-medium mt-1 ${Number(change) >= 0 ? 'text-green-600' : 'text-red-600'}`}
                        >
                            {Number(change) >= 0 ? '+' : ''}
                            {change}%
                        </p>
                    )}
                </div>
            )
        }
        return null
    }

    return (
        <div className="space-y-2" ref={chartRef}>
            <div className="flex items-center justify-between">
                <Typogaphy size="small" className="font-medium text-gray-700">
                    {t('employees.performance.revenue_breakdown_by_type')}
                </Typogaphy>
                <Button
                    size="small"
                    variant="text"
                    onClick={exportChartToPDF}
                    className="!p-1"
                >
                    <Download className="w-4 h-4" />
                </Button>
            </div>

            {/* Show grouped bar chart when comparing, stacked when not */}
            {hasComparison ? (
                <ResponsiveContainer width="100%" height={450}>
                    <BarChart
                        data={combinedData}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 60,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                            dataKey="name"
                            height={80}
                            tick={<CustomXAxisTick />}
                            interval={0}
                        />
                        <YAxis
                            tickFormatter={formatYAxis}
                            tick={{ fontSize: 12 }}
                            width={60}
                        />
                        <Tooltip content={<ComparisonTooltip />} />
                        <Legend
                            style={{ fontSize: '12px' }}
                            wrapperStyle={{
                                paddingTop: '10px',
                                paddingBottom: '20px',
                            }}
                            iconType="circle"
                            verticalAlign="top"
                        />
                        <Bar dataKey="current" radius={[4, 4, 0, 0]}>
                            {combinedData.map((entry, index) => (
                                <Cell
                                    key={`cell-current-${index}`}
                                    fill={entry.color}
                                />
                            ))}
                        </Bar>
                        <Bar dataKey="comparison" radius={[4, 4, 0, 0]}>
                            {combinedData.map((entry, index) => (
                                <Cell
                                    key={`cell-comp-${index}`}
                                    fill={entry.color}
                                    opacity={0.4}
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <ResponsiveContainer width="100%" height={450}>
                    <BarChart
                        data={data}
                        margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 60,
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                            dataKey="name"
                            height={80}
                            tick={<CustomXAxisTick />}
                            interval={0}
                        />
                        <YAxis
                            tickFormatter={formatYAxis}
                            tick={{ fontSize: 12 }}
                            width={60}
                        />
                        <Tooltip content={<CustomBarTooltip />} />
                        <Legend
                            style={{ fontSize: '12px' }}
                            wrapperStyle={{
                                paddingTop: '10px',
                                paddingBottom: '20px',
                            }}
                            iconType="circle"
                            verticalAlign="top"
                        />
                        <Bar
                            dataKey="renewal"
                            name={t('employees.performance.renewal')}
                            stackId="a"
                            fill={CHART_COLORS.EMERALD_GREEN}
                            radius={[0, 0, 0, 0]}
                        />
                        <Bar
                            dataKey="newSale"
                            name={t('employees.performance.new_sale')}
                            stackId="a"
                            fill={CHART_COLORS.PRIMARY_BLUE}
                            radius={[0, 0, 0, 0]}
                        />
                        <Bar
                            dataKey="upsell"
                            name={t('employees.performance.upsell')}
                            stackId="a"
                            fill={CHART_COLORS.INDIGO_PURPLE}
                            radius={[4, 4, 0, 0]}
                        />
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    )
}

export default RevenueBreakdownChart
